import { ApplicationConfig } from "@angular/core";
import { getApp, initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { environment } from "../../environments/environment";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  provideAuth,
} from "@angular/fire/auth";
import { connectStorageEmulator, getStorage, provideStorage } from "@angular/fire/storage";
import { connectFunctionsEmulator, getFunctions, provideFunctions } from "@angular/fire/functions";
import {
  connectFirestoreEmulator,
  initializeFirestore,
  provideFirestore,
} from "@angular/fire/firestore";
import { Capacitor } from "@capacitor/core";
import { enableLogging } from "@angular/fire/database";
import { getAnalytics, provideAnalytics } from "@angular/fire/analytics";
import { getRemoteConfig, provideRemoteConfig } from "@angular/fire/remote-config";

export const appFirebaseConfig: ApplicationConfig = {
  providers: [
    provideFirebaseApp(() => {
      const firebaseApp = initializeApp(environment.firebase);
      const auth = initializeAuth(firebaseApp, {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence],
      });

      if (environment.emulators.auth) {
        connectAuthEmulator(auth, "http://localhost:9099");
      }
      if (environment.emulators.storage) {
        connectStorageEmulator(getStorage(firebaseApp), "localhost", 9199);
      }
      if (environment.emulators.functions) {
        connectFunctionsEmulator(
          getFunctions(firebaseApp, environment.appConfig.ngFnsOrigin),
          "localhost",
          5001,
        );
      }
      if (environment.emulators.firestore) {
        connectFirestoreEmulator(
          initializeFirestore(firebaseApp, firestoreSettings()),
          "localhost",
          8080,
        );
      }

      if (Capacitor.isNativePlatform()) {
        enableLogging(true);
      }
      return firebaseApp;
    }),
    provideRemoteConfig(() => getRemoteConfig(getApp())),
    provideAuth(() => getAuth(getApp())),
    provideStorage(() => getStorage(getApp())),
    provideFunctions(() => getFunctions(getApp(), environment.appConfig.ngFnsOrigin)),
    provideAnalytics(() => getAnalytics(getApp())),
    provideFirestore(() => initializeFirestore(getApp(), firestoreSettings())),
  ],
};

/**
 * Safari refuses the Firestore streaming (Listen) channel outright
 * ("Fetch API cannot load .../Listen/channel ... due to access control
 * checks"), so every realtime subscription hangs until the SDK falls back to
 * long polling — a ~minute penalty per fresh channel. Auto-detect still pays
 * that penalty on every new channel; on Safari skip streaming from the start.
 */
function firestoreSettings(): Parameters<typeof initializeFirestore>[1] {
  if (requiresForcedLongPolling()) {
    return { experimentalForceLongPolling: true };
  }
  return { experimentalAutoDetectLongPolling: true };
}

function requiresForcedLongPolling(): boolean {
  // Native iOS is WKWebView (same broken streaming channel), regardless of UA overrides.
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    return true;
  }
  if (typeof navigator === 'undefined') {
    return false;
  }
  // Safari UA contains "Safari" but not "Chrome"/"Chromium"/"Android"; this
  // intentionally includes iOS Chrome/Firefox (all WebKit, same limitation).
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}
